import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { type EventListeners } from "@/hooks/useEventListeners";
import { useLiveQuery } from "dexie-react-hooks";
import { getToken } from "@/lib/keycloak";
import { ttsDb, setTTSMuted } from "@/db/ttsDatabase";
import { useSessionState } from "@/hooks/useSessionState";
import { useTTSPlayback, type TTSPlaybackEvents } from "./useTTSPlayback";
import { useTTSCapture } from "./useTTSCapture";
import type { TTSRef } from "./types";

// ============================================================================
// Context
//
// The provider is a thin shell. The pipeline is two decoupled halves wired
// together here by exactly one type (TTSChunk):
//
//   collect tokens → create chunks   (useTTSCapture)   — data-driven, no audio
//   request → queue → play audio     (useTTSPlayback)  — audio-driven, no bus
//
// Capture emits chunks; playback consumes them. Neither knows the other exists.
// ============================================================================

type TTSEvents = TTSPlaybackEvents;

interface TTSContextValue {
  speak(refs: TTSRef[], opts?: { streaming?: boolean }): void;
  speakText(text: string, voice: string): void;
  stop(): void;
  isPlaying: boolean;
  currentEntryId: string | null;
  /** The entry whose audio is playing NOW (null during the queued/loading gap). */
  playingEntryId: string | null;
  events: EventListeners<TTSEvents>;
  isTTSMuted(workspaceId: string, userProfileId: string): boolean;
  toggleTTSMute(workspaceId: string, userProfileId: string): void;
  isAutoSpeak(workspaceId: string): boolean;
  setAutoSpeak(workspaceId: string, on: boolean): void;
}

const TTSContext = createContext<TTSContextValue | null>(null);

export const TTSProvider: FC<PropsWithChildren> = ({ children }) => {
  // Auto-speak preference, persisted per workspace across sessions.
  const [autoSpeakMap, setAutoSpeakMap] = useSessionState<Record<string, boolean>>("tts:autoSpeak", {});

  // Mute state from Dexie, mirrored to a ref for use in callbacks.
  const mutedSetRef = useRef(new Set<string>());
  const muteRecords = useLiveQuery(() => ttsDb.ttsMutes.toArray(), []);
  const mutedSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of muteRecords ?? []) {
      if (r.muted) s.add(`${r.workspaceId}:${r.userProfileId}`);
    }
    return s;
  }, [muteRecords]);
  mutedSetRef.current = mutedSet;

  const isMuted = useCallback(
    (workspaceId: string, userProfileId: string) => mutedSetRef.current.has(`${workspaceId}:${userProfileId}`),
    [],
  );

  // --- Playback (request → queue → play) ------------------------------------
  const playback = useTTSPlayback();

  // --- Capture (collect tokens → create chunks) -----------------------------
  const capture = useTTSCapture({
    emitChunk: playback.enqueueChunk,
    addProducer: playback.addProducer,
    removeProducer: playback.removeProducer,
    isMuted,
  });

  // --- Wiring: speak/stop ----------------------------------------------------
  const stopImplRef = useRef<() => void>(() => {});
  stopImplRef.current = () => {
    capture.detachAll();
    playback.stopPlayback();
  };

  const speakImplRef = useRef<(refs: TTSRef[], opts?: { streaming?: boolean }) => void>(() => {});
  speakImplRef.current = (refs: TTSRef[], opts?: { streaming?: boolean }) => {
    const streaming = !!opts?.streaming;
    if (!streaming) {
      // Manual action (speak / play-from-here): interrupt and replace.
      capture.detachAll();
    }
    playback.beginSession(!streaming);
    // Light the clicked message immediately (intent), before any async work.
    if (refs[0]) playback.setIntentEntry(refs[0].entryId);
    void capture.captureRefs(refs, streaming, playback.getSignal());
  };

  const speakTextImplRef = useRef<(text: string, voice: string) => void>(() => {});
  speakTextImplRef.current = (text: string, voice: string) => {
    stopImplRef.current();
    if (!getToken()) {
      console.warn("[TTS] No access token — cannot speak");
      return;
    }
    playback.beginSession(true);
    playback.setIntentEntry("preview");
    capture.captureText(text, voice);
  };

  // --- Stable wrappers -------------------------------------------------------
  const stop = useCallback(() => stopImplRef.current(), []);
  const speak = useCallback((refs: TTSRef[], opts?: { streaming?: boolean }) => speakImplRef.current(refs, opts), []);
  const speakText = useCallback((text: string, voice: string) => speakTextImplRef.current(text, voice), []);

  const isTTSMuted = useCallback(
    (workspaceId: string, userProfileId: string) => mutedSetRef.current.has(`${workspaceId}:${userProfileId}`),
    [],
  );
  const toggleTTSMute = useCallback((workspaceId: string, userProfileId: string) => {
    const key = `${workspaceId}:${userProfileId}`;
    const current = mutedSetRef.current.has(key);
    void setTTSMuted(workspaceId, userProfileId, !current);
  }, []);
  const isAutoSpeak = useCallback((workspaceId: string) => !!autoSpeakMap[workspaceId], [autoSpeakMap]);
  const setAutoSpeak = useCallback(
    (workspaceId: string, on: boolean) => {
      if (on) {
        // Auto-speak is mutually exclusive across workspaces: enabling one turns
        // every other off and flushes whatever is currently playing.
        setAutoSpeakMap({ [workspaceId]: true });
        stopImplRef.current();
      } else {
        // Disabling only clears this workspace — no stop, no flush.
        setAutoSpeakMap((prev) => ({ ...prev, [workspaceId]: false }));
      }
    },
    [setAutoSpeakMap],
  );

  // Cleanup on unmount.
  const disposeRef = useRef(playback.dispose);
  disposeRef.current = playback.dispose;
  useEffect(() => {
    return () => disposeRef.current();
  }, []);

  const { isPlaying, currentEntryId, playingEntryId, events } = playback;
  const contextValue = useMemo<TTSContextValue>(
    () => ({
      speak,
      speakText,
      stop,
      isPlaying,
      currentEntryId,
      playingEntryId,
      events,
      isTTSMuted,
      toggleTTSMute,
      isAutoSpeak,
      setAutoSpeak,
    }),
    [speak, speakText, stop, isPlaying, currentEntryId, playingEntryId, events, isTTSMuted, toggleTTSMute, isAutoSpeak, setAutoSpeak],
  );

  return <TTSContext.Provider value={contextValue}>{children}</TTSContext.Provider>;
};

// ============================================================================
// Hooks
// ============================================================================

export const useTTS = (): TTSContextValue => {
  const ctx = useContext(TTSContext);
  if (!ctx) throw new Error("useTTS must be used within a TTSProvider");
  return ctx;
};

export const useTTSOptional = (): TTSContextValue | null => {
  return useContext(TTSContext);
};
